import { defineStore } from 'pinia'

import api from '../services/api' // ton service axios

import axios from 'axios'

import apiClient from '../services/axios'

import { CDNImageFrontDecrypt } from '../services/media/DecryptImage'

import {
  getAdminAuthenticateInFlight,
  invalidateAdminAuthCache,
  markAdminAuthCacheSuccess,
  resolveAdminAuthToken,
  setAdminAuthenticateInFlight,
  shouldSkipAdminAuthenticate,
} from '../utils/adminAuthCache'

export { invalidateAdminAuthCache } from '../utils/adminAuthCache'

const buildDicebearAvatarUrl = (username: string) =>
  `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(username || 'user')}`

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  if (!token) return null

  try {
    const [, payload = ''] = token.split('.')
    if (!payload) return null

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)

    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

type AdminProfileState = {
  userName: string
  avatar: string
  id: string
  role: string
  code: string
  parentId: string
  iat: string
  can_enable_free_video: boolean,
}

const applyProvisionalProfileFromToken = (token: string, state: AdminProfileState) => {
  const payload = decodeJwtPayload(token)
  const username = typeof payload?.username === 'string' ? payload.username : ''

  if (!username) return

  state.userName = username
  state.id = typeof payload?.id === 'string' ? payload.id : ''
  state.role = typeof payload?.role === 'string' ? payload.role : ''
  state.code = typeof payload?.code === 'string' ? payload.code : ''
  state.parentId =
    typeof payload?.parent_id === 'string'
      ? payload.parent_id
      : payload?.parent_id == null
        ? ''
        : String(payload.parent_id)
  state.iat =
    typeof payload?.iat === 'number'
      ? new Date(payload.iat * 1000).toLocaleDateString('fr-FR')
      : ''
  state.can_enable_free_video = typeof payload?.can_enable_free_video === 'boolean' ? payload.can_enable_free_video : false // NOUVEAU
  state.avatar = buildDicebearAvatarUrl(username)
}

const loadCustomAvatarInBackground = (
  state: AdminProfileState,
  username: string,
  userId: string,
  role: string,
) => {
  void (async () => {
    try {
      let avatarHttp = ''

      if (role === 'agent' && userId) {
        const { data } = await apiClient.post(api.agentDetails(userId))
        if (data?.avatar && String(data.avatar).startsWith('http')) {
          avatarHttp = String(data.avatar)
        }
      } else {
        // Search in admin/agent list for avatar
        const { data: admins } = await apiClient.post(api.listAdmins())
        const list = Array.isArray(admins) ? admins : (admins.data || [])
        const me = list.find((a: { username?: string }) => a.username === username)

        if (me?.avatar && String(me.avatar).startsWith('http')) {
          avatarHttp = String(me.avatar)
        }
      }

      if (!avatarHttp || state.userName !== username) return

      const blob = await CDNImageFrontDecrypt(avatarHttp)
      state.avatar = URL.createObjectURL(blob)
    } catch {
      // Fallback: silent fail, will use dicebear
    }
  })()
}

export const useAdminStore = defineStore('admin', {

  state: () => ({

    id: '',

    userName: '',

    role: '',

    avatar: '',

    code: '',

    parentId: '',

    iat: '',
    can_enable_free_video: false,

  }),

  getters: {
    currentId: (state) => state.id,
  },



  actions: {

    async getAdminAuthenticate(options?: { force?: boolean }) {

      const force = options?.force === true
      const token = resolveAdminAuthToken()

      // Cache mémoire : évite authenticate + all + playphoto en double (layout / navigation)
      if (shouldSkipAdminAuthenticate(Boolean(this.userName), force)) {
        return
      }

      const inFlight = getAdminAuthenticateInFlight()
      if (inFlight && !force) {
        return inFlight
      }

      if (force) {
        invalidateAdminAuthCache()
      }

      const authenticatePromise = (async () => {

      // Check if this tab is an impersonation session

      const sessionData = sessionStorage.getItem('impersonation_session')

      if (sessionData) {

        try {

          const agent = JSON.parse(sessionData)

          // Try to fetch the real avatar for the impersonated agent

          this.$state = {

            id: agent.id || '',

            userName: agent.username || '',

            role: agent.role || 'agent',

            avatar: buildDicebearAvatarUrl(agent.username || ''),

            code: agent.code || '',

            parentId: agent.parent_id || '',

            iat: '',
            can_enable_free_video: false,

          }

          void (async () => {
            try {

              const impToken = sessionStorage.getItem('impersonation_token') || ''

              const { data: agentData } = await apiClient.post(api.getAuth(), {

                headers: { Authorization: `Bearer ${impToken}` },

              })

              const resolvedUsername = agentData.username || agent.username || ''
              const resolvedId = agentData.id || agent.id || ''
              const resolvedRole = agentData.role || agent.role || 'agent'

              loadCustomAvatarInBackground(this.$state, resolvedUsername, resolvedId, resolvedRole)

            } catch {

              // Fallback: silent fail, will use dicebear

            }
          })()

          return

        } catch {

          sessionStorage.removeItem('impersonation_session')

        }

      }



      // Récupérer le token selon le storage (sessionStorage pour agent, localStorage pour admin)

      const authToken = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token') || ''

      applyProvisionalProfileFromToken(authToken, this.$state)



      try {

        const { data } = await apiClient.post(api.getAuth(), {

          headers: { Authorization: `Bearer ${authToken}` },

        })



        // Fetch full admin profile to get avatar 

        this.$state = {

          id: data.id,

          userName: data.username,

          role: data.role,

          avatar: buildDicebearAvatarUrl(data.username),

          code: data.code,

          parentId: data.parent_id,

          iat: new Date(data.iat * 1000).toLocaleDateString('fr-FR'),
          can_enable_free_video: data.can_enable_free_video || false,

        }

        loadCustomAvatarInBackground(this.$state, data.username, data.id, data.role)

      } catch (error) {

        console.error('Erreur authenticate admin :', error)

      }

      })()

      setAdminAuthenticateInFlight(authenticatePromise)

      try {
        await authenticatePromise
        markAdminAuthCacheSuccess(token)
      } finally {
        setAdminAuthenticateInFlight(null)
      }

    },

  },

})
